/**
 * TabsProvider - OpenEMR-style workbench tabs.
 *
 * - A tab is opened automatically whenever the route changes.
 * - The Dashboard tab is always present and cannot be closed.
 * - All other tabs can be closed. Closing the active tab
 *   navigates to the tab immediately to its left (or Dashboard).
 * - Tabs persist in sessionStorage so a browser refresh keeps them.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Users,
  ClipboardList,
  MessageSquare,
  Target,
  FileText,
  CreditCard,
  Shield,
  Plug,
  Settings,
  Rows3,
  Activity,
  Pill,
  Syringe,
  Stethoscope,
  CheckSquare,
  FilePenLine,
  User,
  type LucideIcon,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Tab {
  id: string;       // unique - based on the full path
  path: string;     // the href to navigate to
  label: string;    // display name shown in the tab bar
  icon: LucideIcon; // icon component
  pinned?: boolean; // pinned tabs can't be closed
}

interface TabsContextValue {
  tabs: Tab[];
  activeId: string;
  openTab: (path: string) => void;
  closeTab: (id: string) => void;
  focusTab: (id: string) => void;
}

// ── Route → Tab meta mapping ──────────────────────────────────────────────────

interface RouteMeta {
  label: string;
  icon: LucideIcon;
  /** If true, we extract a dynamic label segment (e.g. patient name) later. */
  dynamic?: boolean;
}

type RoutePattern = [RegExp, RouteMeta];

/** One workbench tab for the whole settings module; label updates per sub-route. */
const SETTINGS_TAB_ID = "/settings";

function isSettingsPath(path: string): boolean {
  return path === "/settings" || path.startsWith("/settings/");
}

const ROUTE_PATTERNS: RoutePattern[] = [
  [/^\/account/, { label: "My Account", icon: User }],
  [/^\/settings\/users\/new/, { label: "New User", icon: Users }],
  [/^\/settings\/users\/[^/]+\/permissions/, { label: "User Permissions", icon: Shield }],
  [/^\/settings\/users\/[^/]+/, { label: "User Detail", icon: Users }],
  [/^\/settings\/users/, { label: "Users", icon: Users }],
  [/^\/settings\/providers\/[^/]+/, { label: "Provider Detail", icon: User }],
  [/^\/settings\/providers/, { label: "Providers", icon: User }],
  [/^\/settings\/integrations\/claimrev/, { label: "ClaimRev", icon: Plug }],
  [/^\/settings\/integrations\/stripe/, { label: "Stripe Connect", icon: Plug }],
  [/^\/settings\/integrations\/eprescribing/, { label: "ePrescribing", icon: Plug }],
  [/^\/settings\/integrations\/exchange-partners/, { label: "Exchange Partners", icon: Plug }],
  [/^\/settings\/integrations/, { label: "Integrations", icon: Plug }],
  [/^\/settings\/config/, { label: "Configuration", icon: Settings }],
  [/^\/settings\/organization/, { label: "Practice Info", icon: Settings }],
  [/^\/settings\/locations/, { label: "Locations", icon: Settings }],
  [/^\/settings\/roles/, { label: "Roles & Permissions", icon: Shield }],
  [/^\/settings\/security/, { label: "Security & privacy", icon: Shield }],
  [/^\/settings\/audit/, { label: "Audit Logs", icon: ClipboardList }],
  [/^\/settings\/preferences/, { label: "Preferences", icon: Settings }],
  [/^\/settings\/notifications/, { label: "Notifications", icon: Settings }],
  [/^\/settings\/?$/, { label: "Settings Home", icon: Settings }],
  [/^\/dashboard$/, { label: "Dashboard", icon: LayoutDashboard }],
  [/^\/tasks/, { label: "Tasks", icon: CheckSquare }],
  [/^\/open-notes/, { label: "Open Notes", icon: FilePenLine }],
  [/^\/pending-cosigns/, { label: "Pending Co-Signs", icon: FilePenLine }],
  [/^\/forms\/builder/, { label: "Form Builder", icon: FileText }],
  [/^\/forms/, { label: "Forms & Intake", icon: ClipboardList }],
  [/^\/schedule/, { label: "Schedule", icon: Calendar }],
  [/^\/patients\/new/, { label: "New Patient", icon: Users }],
  [/^\/patients\/[^/]+\/encounters/, { label: "Encounters", icon: Stethoscope, dynamic: true }],
  [/^\/patients\/[^/]+\/billing/, { label: "Billing", icon: CreditCard, dynamic: true }],
  [/^\/patients\/[^/]+\/medications/, { label: "Medications", icon: Pill, dynamic: true }],
  [/^\/patients\/[^/]+\/immunizations/, { label: "Immunizations", icon: Syringe, dynamic: true }],
  [/^\/patients\/[^/]+\/care-plans/, { label: "Care Plans", icon: ClipboardList, dynamic: true }],
  [/^\/patients\/[^/]+/, { label: "Patient Chart", icon: ClipboardList, dynamic: true }],
  [/^\/patients/, { label: "Patients", icon: Users }],
  [/^\/encounters\//, { label: "Encounter", icon: Activity }],
  [/^\/tracker/, { label: "Patient Tracker", icon: Rows3 }],
  [/^\/messages\/[^/]+/, { label: "Message", icon: MessageSquare, dynamic: true }],
  [/^\/messages/, { label: "Messages", icon: MessageSquare }],
  [/^\/prescriptions/, { label: "Prescriptions", icon: Pill }],
  [/^\/billing/, { label: "Billing", icon: CreditCard }],
  [/^\/reports/, { label: "Reports", icon: FileText }],
  [/^\/decision-support/, { label: "CDS Alerts", icon: Shield }],
  [/^\/analytics/, { label: "Analytics", icon: Target }],
];

function resolveRouteMeta(path: string): RouteMeta {
  for (const [pattern, meta] of ROUTE_PATTERNS) {
    if (pattern.test(path)) return meta;
  }
  return { label: "Page", icon: FileText };
}

/** Build a stable tab ID from the path (path without query/hash). */
function tabId(path: string): string {
  return path.split("?")[0] ?? path;
}

// ── Dashboard (pinned) seed tab ───────────────────────────────────────────────

const DASHBOARD_TAB: Tab = {
  id: "/dashboard",
  path: "/dashboard",
  label: "Dashboard",
  icon: LayoutDashboard,
  pinned: true,
};

function loadTabs(): Tab[] {
  try {
    const raw = sessionStorage.getItem("ehr_tabs");
    if (raw) {
      const parsed: Array<Omit<Tab, "icon"> & { iconKey: string }> = JSON.parse(raw);
      const mapped = parsed.map((t) => {
        const meta = resolveRouteMeta(t.path);
        return {
          ...t,
          label: meta.label,
          icon: meta.icon,
          pinned: t.path === "/dashboard",
        };
      });

      // Collapse legacy duplicate settings tabs into one.
      const settingsTabs = mapped.filter((t) => isSettingsPath(t.path));
      const otherTabs = mapped.filter((t) => !isSettingsPath(t.path));
      if (settingsTabs.length > 1) {
        const latest = settingsTabs[settingsTabs.length - 1]!;
        const meta = resolveRouteMeta(latest.path);
        return [
          ...otherTabs,
          {
            id: SETTINGS_TAB_ID,
            path: latest.path,
            label: meta.label,
            icon: meta.icon,
          },
        ];
      }
      if (settingsTabs.length === 1) {
        const only = settingsTabs[0]!;
        const meta = resolveRouteMeta(only.path);
        return [
          ...otherTabs,
          {
            id: SETTINGS_TAB_ID,
            path: only.path,
            label: meta.label,
            icon: meta.icon,
          },
        ];
      }
      return mapped;
    }
  } catch {
    // ignore
  }
  return [DASHBOARD_TAB];
}

function saveTabs(tabs: Tab[]) {
  try {
    const serializable = tabs.map(({ icon: _icon, ...rest }) => rest);
    sessionStorage.setItem("ehr_tabs", JSON.stringify(serializable));
  } catch {
    // ignore
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

export const TabsContext = createContext<TabsContextValue>({
  tabs: [DASHBOARD_TAB],
  activeId: "/dashboard",
  openTab: () => {},
  closeTab: () => {},
  focusTab: () => {},
});

export function useTabsContext() {
  return useContext(TabsContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function TabsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [tabs, setTabs] = useState<Tab[]>(loadTabs);
  const [activeId, setActiveId] = useState<string>(() => tabId(location.pathname) ?? "/dashboard");

  // Auto-open a tab when the route changes
  const prevPath = useRef<string>("");
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === prevPath.current) return;
    prevPath.current = currentPath;

    const meta = resolveRouteMeta(currentPath);
    const id = isSettingsPath(currentPath) ? SETTINGS_TAB_ID : tabId(currentPath);
    setActiveId(id);

    setTabs((prev) => {
      if (isSettingsPath(currentPath)) {
        const withoutLegacySettings = prev.filter(
          (t) => t.id === SETTINGS_TAB_ID || !isSettingsPath(t.path),
        );
        const existing = withoutLegacySettings.find((t) => t.id === SETTINGS_TAB_ID);
        if (existing) {
          const next = withoutLegacySettings.map((t) =>
            t.id === SETTINGS_TAB_ID
              ? { ...t, path: currentPath, label: meta.label, icon: meta.icon }
              : t,
          );
          saveTabs(next);
          return next;
        }
        const newTab: Tab = {
          id: SETTINGS_TAB_ID,
          path: currentPath,
          label: meta.label,
          icon: meta.icon,
        };
        const next = [...withoutLegacySettings, newTab];
        saveTabs(next);
        return next;
      }

      if (prev.find((t) => t.id === id)) {
        const next = prev.map((t) =>
          t.id === id ? { ...t, path: currentPath, label: meta.label, icon: meta.icon } : t,
        );
        saveTabs(next);
        return next;
      }

      const newTab: Tab = {
        id,
        path: currentPath,
        label: meta.label,
        icon: meta.icon,
        pinned: currentPath === "/dashboard",
      };
      const next = [...prev, newTab];
      saveTabs(next);
      return next;
    });
  }, [location.pathname]);

  // Persist whenever tabs array changes
  useEffect(() => {
    saveTabs(tabs);
  }, [tabs]);

  const openTab = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const closeTab = useCallback((id: string) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;

      const next = prev.filter((t) => t.id !== id);

      // If we closed the active tab, navigate to the one on the left (or dashboard)
      setActiveId((currentActive) => {
        if (currentActive !== id) return currentActive;
        const target = next[Math.max(0, idx - 1)] ?? DASHBOARD_TAB;
        setTimeout(() => navigate(target.path), 0);
        return target.id ?? "/dashboard";
      });

      return next;
    });
  }, [navigate]);

  const focusTab = useCallback((id: string) => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    navigate(tab.path);
    setActiveId(id);
  }, [tabs, navigate]);

  return (
    <TabsContext.Provider value={{ tabs, activeId, openTab, closeTab, focusTab }}>
      {children}
    </TabsContext.Provider>
  );
}
